import { useCallback, useMemo } from 'react';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { EmployeeItemInfoData } from '~/components/GenericList/EmployeeItemInfo';
import { useProcessAgentInfo } from '~/components/WorkflowProposalProcess/components/ProcessAgent/useProcessAgentInfo';
import {
  ApprovalStepState,
  ApprovalStepStatus,
  ProposalAgent,
  ProposalProps,
} from '~/data/proposals';
import { isRoleHRAdmin } from '~/services/Permissions';

type UseWorkflowProposalProcessOutput = {
  proposal: ProposalProps;
  processAgentsInfo: EmployeeItemInfoData[];
  getApprovalAgencyOnStep: (stepSeq: number) => ProposalAgent | undefined;
  approvalAgencyAsUltimate: ProposalAgent | undefined;
  getStepDecision: (
    stepSeq: number,
    newStatus: ApprovalStepStatus,
  ) => ApprovalStepState | undefined;
  getUltimateDecision: (newStatus: ApprovalStepStatus) => ApprovalStepState[];
  hideUltimateAction: boolean;
};

type PropsType = {
  proposal: ProposalProps;
  substituteAsUltimateApprover?: boolean;
  autoCompleteOnResponsibleHR?: boolean;
};

export const useWorkflowProposalProcess = (
  props: PropsType,
): UseWorkflowProposalProcessOutput => {
  const {
    proposal,
    substituteAsUltimateApprover,
    autoCompleteOnResponsibleHR,
  } = props;

  // Logged user data
  const loggedInEmployeeId = useLoggedInEmployeeId();
  const loggedInUserRoles = useLoggedInUserRoles();
  const isHRAdmin = isRoleHRAdmin(loggedInUserRoles);

  // Process related employees info
  const { getAgentsInfoByEmployeeIds } = useProcessAgentInfo();

  const getAllAgentUserIds = useCallback(
    (proposal: ProposalProps): number[] => {
      const getAgentUserId = (agent: ProposalAgent): number => agent.userId;
      const getAgentsUserIds = (agents: ProposalAgent[]): number[] =>
        agents.map((a) => getAgentUserId(a));
      const getApprovalStepUserIds = (step: ApprovalStepState): number[] => {
        return [
          getAgentUserId(step.updatedBy),
          getAgentsUserIds(step.approvers),
        ].flat();
      };

      const onlyUnique = <T>(value: T, index: number, array: T[]) =>
        array.indexOf(value) === index;

      return [
        loggedInEmployeeId,
        getAgentUserId(proposal.initiator),
        getAgentsUserIds(proposal.overseers),
        getAgentsUserIds(proposal.administrators),
        proposal.approvalSteps
          .map((step) => getApprovalStepUserIds(step))
          .flat(),
      ]
        .flat()
        .filter(onlyUnique);
    },
    [loggedInEmployeeId],
  );

  const processAgentsInfo = useMemo(
    () => getAgentsInfoByEmployeeIds(getAllAgentUserIds(proposal)),
    [proposal, getAgentsInfoByEmployeeIds, getAllAgentUserIds],
  );

  // Resolve user agency on proposal process step
  const findApprovalStep = useCallback(
    (steps: ApprovalStepState[], stepSeq: number) => {
      return steps.find((step) => step.seq === stepSeq);
    },
    [],
  );

  const isAgentUser = useCallback(
    (agent: ProposalAgent): boolean => {
      return agent.userId === loggedInEmployeeId;
    },
    [loggedInEmployeeId],
  );

  const findUserInAgents = useCallback(
    (agents: ProposalAgent[]): ProposalAgent | undefined => {
      return agents.find((a) => isAgentUser(a));
    },
    [isAgentUser],
  );

  const usersRoleInProcess = useMemo(() => {
    const approverOnSteps = proposal.approvalSteps.filter((step) => {
      return !!findUserInAgents(step.approvers);
    });

    const administrators: ProposalAgent[] = isHRAdmin
      ? [
          ...proposal.administrators,
          {
            userId: loggedInEmployeeId,
            roleName: 'HR_ADMIN',
          },
        ]
      : proposal.administrators;

    return {
      approverOnSteps,
      administrator: findUserInAgents(administrators),
      overseer: findUserInAgents(proposal.overseers),
    };
  }, [proposal, findUserInAgents, loggedInEmployeeId, isHRAdmin]);

  const getAgency = useCallback(
    (agencyRoles: (ProposalAgent | undefined)[]) => {
      const ars = agencyRoles.filter((role): role is ProposalAgent => !!role);

      return ars.length > 0 ? ars[0] : undefined;
    },
    [],
  );

  const getApprovalAgencyOnStep = useCallback(
    (stepSeq: number): ProposalAgent | undefined => {
      const step = findApprovalStep(
        usersRoleInProcess.approverOnSteps,
        stepSeq,
      );
      const stepApprover = findUserInAgents(step?.approvers || []);

      return getAgency([
        stepApprover,
        usersRoleInProcess.overseer,
        usersRoleInProcess.administrator,
      ]);
    },
    [usersRoleInProcess, findUserInAgents, findApprovalStep, getAgency],
  );

  const hideUltimateAction = useMemo(() => {
    const lastStepApprovers =
      proposal.approvalSteps.slice(-1)[0]?.approvers || [];
    const userIsHRResponsible =
      findUserInAgents(lastStepApprovers)?.roleName === 'RESPONSIBLE_HR';

    return !!autoCompleteOnResponsibleHR && userIsHRResponsible;
  }, [proposal.approvalSteps, findUserInAgents, autoCompleteOnResponsibleHR]);

  const approvalAgencyAsUltimate = useMemo((): ProposalAgent | undefined => {
    return substituteAsUltimateApprover
      ? getAgency([
          usersRoleInProcess.overseer,
          usersRoleInProcess.administrator,
        ])
      : undefined;
  }, [usersRoleInProcess, getAgency, substituteAsUltimateApprover]);

  // Resolve updated step data on decision
  const getStepDecision = useCallback(
    (
      stepSeq: number,
      newStatus: ApprovalStepStatus,
    ): ApprovalStepState | undefined => {
      const agency = getApprovalAgencyOnStep(stepSeq);
      const step = findApprovalStep(proposal.approvalSteps, stepSeq);

      return !!agency && !!step
        ? {
            ...step,
            state: newStatus,
            updatedBy: agency,
            updatedByTimestamp: new Date(),
          }
        : undefined;
    },
    [proposal, getApprovalAgencyOnStep, findApprovalStep],
  );

  const getUltimateDecision = useCallback(
    (newStatus: ApprovalStepStatus): ApprovalStepState[] => {
      if (!approvalAgencyAsUltimate) {
        return [];
      }

      return proposal.approvalSteps
        .filter((step) => step.state === ApprovalStepStatus.PENDING)
        .map((step) => {
          return {
            ...step,
            state: newStatus,
            updatedBy: approvalAgencyAsUltimate,
            updatedByTimestamp: new Date(),
          };
        });
    },
    [proposal, approvalAgencyAsUltimate],
  );

  return {
    proposal,
    processAgentsInfo,
    getApprovalAgencyOnStep,
    approvalAgencyAsUltimate,
    getUltimateDecision,
    getStepDecision,
    hideUltimateAction,
  };
};
